import { useQuery } from '@tanstack/react-query';
import {
	COUNTRIES_LIST,
	COURSES_ENDPOINT,
	INSTANCES_ENDPOINT,
	CATEGORIES_ENDPOINT,
	IMAGE_PATH,
} from '@hooks/constants/countriesList';

/**
 * Handle filters
 * @param {object} options
 * @param {object} options.course - Course data
 * @param {object} options.instance - Instance data
 * @param {object} options.filter - Filter data
 * @return {boolean} - Return true if the instance passes the filters
 */
const handleFilters = ({ course, instance, filter }) => {
	/**
	 * Function returns the UID of the filtered category
	 * Check if the course category is in the filtered categories
	 * Course only has uuid, so we need to find the category by the course uuid
	 * On other filters we can use something else
	 */

	if (
		filter.categories.length > 0 &&
		!filter.categories.includes(course?.category)
	) {
		return false;
	}

	/**
	 * Filter by country
	 * If the country is not in the filter, return null
	 * If the country is in the filter or filter is empty, continue
	 */
	if (
		filter.countries.length > 0 &&
		!filter.countries.includes(instance?.country)
	) {
		return false;
	}

	/**
	 * Filter by venue
	 * If the venue is not in the filter, return null
	 * If the venue is in the filter or filter is empty, continue
	 */
	if (filter.venues.length > 0 && !filter.venues.includes(instance?.venue)) {
		return false;
	}

	/**
	 * Filter by showOnlyConfirmed
	 * If the instance is not confirmed, return null
	 * If the instance is confirmed or filter is false, continue
	 */
	if (
		filter.showOnlyConfirmed === 'true' &&
		instance.status === 'notConfirmed'
	) {
		return false;
	}

	return true;
};

/**
 * Hydrate instance with course and category
 * @param {object} instance
 * @param {array} coursesData
 * @param {array} categoriesData
 * @param {array} availableCountries
 * @param {object} filter
 * @return {object}
 */
const hydrateInstance = (
	instance,
	coursesData,
	categoriesData,
	availableCountries,
	filter
) => {
	const countryName = COUNTRIES_LIST[instance.country];

	// Check if course is enabled in the current site
	const isAvailable = availableCountries.some((country) => {
		if (countryName === 'finland-estonia') {
			return (
				country.toLowerCase() === 'estonia' ||
				country.toLowerCase() === 'finland'
			);
		}

		return country.toLowerCase() === countryName;
	});

	if (!isAvailable) {
		return null;
	}

	const course = coursesData.find((course) => course.uid === instance.model);

	if (!handleFilters({ course, instance, filter })) {
		return null;
	}

	/**
	 * Return the instance with the course and category
	 */
	const category = categoriesData.find(
		(category) => category.uid === course?.category
	);

	return {
		...instance,
		course: {
			...course,
			image_url: `${IMAGE_PATH}${course?.picture}`,
		},
		category: {
			...category,
			image_url: `${IMAGE_PATH}${category?.picture}`,
		},
	};
};

/**
 * Fetch recipes with pagination and search
 * @param {object} options
 * @param {boolean} options.hydrate - Hydrate courses with instances and categories
 * @param {boolean} options.group - Group instances by month and year
 * @param {number} options.perPage - Number of instances to return
 * @param {array} options.countries - List of available countries
 * @returns {object}
 */
function useCourses({
	hydrate = false,
	group = false,
	availableCountries = [],
	filter = {
		categories: [],
		countries: [],
		venues: [],
		showOnlyConfirmed: false,
	},
	perPage = 0,
}) {
	const { isPending, isError, data, error } = useQuery({
		queryKey: ['courses', filter],
		queryFn: async () => {
			/**
			 * API returns all courses without instances
			 * If hydrate is false, return all courses
			 */
			if (!hydrate) {
				const courseResponse = await fetch(COURSES_ENDPOINT);

				return await courseResponse.json();
			}

			/**
			 * Else hydrate courses with instances and categories
			 */
			const [courses, instances, categories] = await Promise.all([
				fetch(COURSES_ENDPOINT),
				fetch(INSTANCES_ENDPOINT),
				fetch(CATEGORIES_ENDPOINT),
			]);

			const [coursesData, instancesData, categoriesData] =
				await Promise.all([
					courses.json(),
					instances.json(),
					categories.json(),
				]);

			/**
			 * If group is true, group instances by month and year
			 */
			if (group) {
				const options = { month: 'long', year: 'numeric' };
				const formatter = new Intl.DateTimeFormat('en-US', options);

				const groupedInstances = instancesData.reduce(
					(acc, instance) => {
						const date = new Date(instance.start);
						const key = `${formatter.format(date)}`;
						if (!acc[key]) {
							acc[key] = [];
						}
						acc[key].push(instance);
						return acc;
					},
					{}
				);

				const groupedData = Object.keys(groupedInstances).map((key) => {
					return {
						key,
						instances: groupedInstances[key]
							.map((instance) => {
								return hydrateInstance(
									instance,
									coursesData,
									categoriesData,
									availableCountries,
									filter
								);
							})
							.filter(Boolean),
					};
				});

				return groupedData;
			}

			// Merge instances with related courses
			const hydratedData = instancesData
				.map((instance) => {
					return hydrateInstance(
						instance,
						coursesData,
						categoriesData,
						availableCountries,
						filter
					);
				})
				.filter(Boolean);

			// If perPage is set (larger than 0), return only the first n instances
			if (perPage > 0) {
				return hydratedData
					.sort((a, b) => a.start - b.start)
					.slice(0, perPage);
			}

			// Return all instances
			return hydratedData.sort((a, b) => a.start - b.start);
		},
	});

	return {
		data,
		error,
		isError,
		isPending,
	};
}

export { useCourses };
